import { FileCopyRounded, Share, WhatsApp } from '@mui/icons-material'
import { Alert, Button, Divider, IconButton, MenuItem, Snackbar, Tooltip, Typography } from '@mui/material'
import { MouseEvent } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import useShareActions from '../../hooks/useShareActions'
import { ShareIconProps } from '../../models/ShareIcon'
import MenuMobileItem from '../../styles/MenuMobileItem'
import { ButtonTextGreen, ShareItemContainer, ShareMenu } from '../../styles/ShareIconStyles'

function ShareIcon({
  meta = false,
  visibility,
  component = 'button',
  light = false,
  title,
  description,
  link,
  type,
  multimedia = '',
  multimediaType = 'image',
  buttonText = '',
  buttonTextGreen = '',
  foreignId = 0
}: ShareIconProps) {
  const { t } = useTranslation(['global'])

  const {
    anchorOp,
    openAlert,
    openOptions,
    handleClickWhatsapp,
    handleShareButton,
    handleCloseOptions,
    handleCloseAlert,
    handleNavigatorShare
  } = useShareActions(title, description, link, type, light, foreignId)

  let isLight = light
  return (
    <>
      {meta && (
        <Helmet>
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:url" content={link} />
          <meta property={`og:${multimediaType}`} content={multimedia} />
          <meta property={`og:"${multimediaType}:secure_url`} content={multimedia} />
          <meta property={`og:"${multimediaType}:width`} content="400" />
          <meta property={`og:"${multimediaType}:height`} content="300" />
        </Helmet>
      )}

      {component === 'button' && (
        <Tooltip title={`${t('share')}`} placement="bottom">
          <IconButton
            aria-controls="share-link-menu"
            aria-haspopup="false"
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              handleShareButton(event)
            }}
            className="share-options"
          >
            <Share
              sx={theme => (isLight ? { color: theme.palette.grey[50] } : { color: theme.palette.grey[300] })}
              style={isLight ? { fontSize: 20 } : {}}
            />
          </IconButton>
        </Tooltip>
      )}
      {component === 'buttonText' && (
        <Button
          sx={theme => ({
            height: 42,
            color: theme.palette.grey[50],
            backgroundColor: theme.palette.grey[600]
          })}
          variant="contained"
          className="share-options"
          startIcon={<Share />}
          aria-controls="share-link-menu"
          aria-haspopup="false"
          size="small"
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            handleShareButton(event)
          }}
        >
          {buttonText}
        </Button>
      )}
      {component === 'buttonTextGreen' && (
        <ButtonTextGreen
          variant="text"
          className="share-options"
          startIcon={<Share />}
          aria-controls="share-link-menu"
          aria-haspopup="false"
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            handleShareButton(event)
          }}
        >
          <Typography variant="body2">{buttonTextGreen}</Typography>
        </ButtonTextGreen>
      )}
      {component === 'menuItem' && (
        <MenuMobileItem
          classes={isLight ? { root: 'client' } : {}}
          aria-controls="share-link-menu"
          aria-haspopup="false"
          onClick={(event: MouseEvent<HTMLLIElement>) => {
            handleShareButton(event)
          }}
          className="share-options"
        >
          {isLight ? (
            <>
              <Share fontSize="small" />
              {t('share-current-url')}
            </>
          ) : (
            t('share')
          )}
        </MenuMobileItem>
      )}
      {/* biome-ignore lint/complexity/noUselessFragments: <explanation> */}
      {component === 'none' && <></>}

      <ShareMenu
        id="share-link-menu"
        anchorEl={anchorOp}
        keepMounted
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openOptions}
        onClose={handleCloseOptions}
        onClick={handleCloseOptions}
      >
        <ShareItemContainer>
          {visibility === 2 && (
            <MenuItem>
              <iframe
                title="facebook"
                src={`https://www.facebook.com/plugins/share_button.php?href=${encodeURIComponent(link)}&layout=button&size=small&appId=771862063439825&width=89&height=20`}
                width="89"
                height="20"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
              />
            </MenuItem>
          )}
          {/* <Tooltip title={`${t('share-on-whatsapp')}`} placement="bottom">
                        <MenuItem onClick={handleClickWhatsapp}>
                            <WhatsApp fontSize="small" />
                        </MenuItem>
                    </Tooltip>
                    <Tooltip title={`${t('copy-link')}`} placement="bottom">
                        <MenuItem onClick={(event: MouseEvent<HTMLLIElement>) => { handleShareButton(event) }}>
                            <FileCopyRounded fontSize="small" />
                        </MenuItem>
                    </Tooltip> */}
          <MenuItem onClick={handleClickWhatsapp}>
            <WhatsApp fontSize="small" />
            <Typography variant="caption">{t('share-on-whatsapp')}</Typography>
          </MenuItem>
          <Divider />
          <MenuItem
            onClick={(_: MouseEvent<HTMLLIElement>) => {
              handleNavigatorShare()
            }}
          >
            <FileCopyRounded fontSize="small" />
            <Typography variant="caption">{t('copy-link')}</Typography>
          </MenuItem>
        </ShareItemContainer>
      </ShareMenu>

      <Snackbar
        open={openAlert}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success">{t('text-copied-success')}</Alert>
      </Snackbar>
    </>
  )
}

export default ShareIcon
